// JARVIS Strategy Lifecycle Engine
// Manages the complete lifecycle of every strategy:
// NEW → TESTING → PROVEN → ELITE → HALL_OF_FAME (or → RETIRED)
// Also handles: DNA detection, version control, similarity merge, auto-retire

import { secureGet, secureSet } from '../utils/encryptionUtils';
import { getStrategies, STRATEGY_STATUS } from './jarvisBrain';

// ── Extended status (adds ELITE + HALL_OF_FAME to jarvisBrain statuses) ──────
export const LIFECYCLE_STATUS = {
  ...STRATEGY_STATUS,           // HYPOTHESIS, TESTING, PROVEN, STRONG (= ELITE), RETIRED
  ELITE:         'ELITE',       // 20+ trades, ≥ 65% win rate
  HALL_OF_FAME:  'HALL_OF_FAME',// 30+ trades, ≥ 75% win rate
};

// Promotion thresholds
const THRESHOLDS = [
  { status: LIFECYCLE_STATUS.HALL_OF_FAME, minTrades: 30, minWR: 75 },
  { status: LIFECYCLE_STATUS.ELITE,        minTrades: 20, minWR: 65 },
  { status: LIFECYCLE_STATUS.PROVEN,       minTrades: 10, minWR: 55 },
  { status: LIFECYCLE_STATUS.TESTING,      minTrades:  3, minWR:  0 },
];

// Demotion thresholds (win rate dropped)
const DEMOTE_MAP = {
  [LIFECYCLE_STATUS.HALL_OF_FAME]: { fallTo: LIFECYCLE_STATUS.ELITE,  belowWR: 65 },
  [LIFECYCLE_STATUS.ELITE]:        { fallTo: LIFECYCLE_STATUS.PROVEN,  belowWR: 50 },
  [LIFECYCLE_STATUS.PROVEN]:       { fallTo: LIFECYCLE_STATUS.TESTING, belowWR: 40 },
};

// Auto-retire: if win rate falls below this after min trades
const RETIRE_THRESHOLD = { minTrades: 10, maxWR: 35 };

// ── Storage key for lifecycle metadata ───────────────────────────────────────
const SK_LIFECYCLE = 'jarvis_strategy_lifecycle';

function getLifecycleMeta() {
  return secureGet(SK_LIFECYCLE, {});
}
function setLifecycleMeta(meta) {
  secureSet(SK_LIFECYCLE, meta);
}

// ── Promote / Demote / Retire ─────────────────────────────────────────────────

export function evaluateStrategyLifecycle(strategyId) {
  const strategies = getStrategies();
  const s = strategies.find(st => st.id === strategyId);
  if (!s) return null;

  const { win_rate: wr, total_trades: total, status: current } = s;

  // Check auto-retire
  if (total >= RETIRE_THRESHOLD.minTrades && wr <= RETIRE_THRESHOLD.maxWR) {
    return { action: 'RETIRE', reason: `Win rate ${wr}% is below 35% after ${total} trades` };
  }

  // Check demotion first (win rate dropped)
  const demote = DEMOTE_MAP[current];
  if (demote && wr < demote.belowWR && total >= 10) {
    return { action: 'DEMOTE', newStatus: demote.fallTo, reason: `Win rate dropped to ${wr}%` };
  }

  // Check promotion
  for (const threshold of THRESHOLDS) {
    if (threshold.status === current) break; // Already at or above
    if (total >= threshold.minTrades && wr >= threshold.minWR) {
      return { action: 'PROMOTE', newStatus: threshold.status, reason: `${total} trades, ${wr}% win rate` };
    }
  }

  return { action: 'NO_CHANGE' };
}

// ── Strategy DNA detection ────────────────────────────────────────────────────
// Reads strategy title + conditions and assigns it a concept category.
// This determines WHICH type of dynamic agent will be spawned for it.

const DNA_PATTERNS = [
  { concept: 'ORDER_BLOCK',  keywords: ['ob', 'order block', 'orderblock', 'bullish ob', 'bearish ob', 'demand zone', 'supply zone'] },
  { concept: 'FVG',          keywords: ['fvg', 'fair value gap', 'imbalance', 'ifvg', 'bisi', 'sibi'] },
  { concept: 'STRUCTURE',    keywords: ['bos', 'choch', 'break of structure', 'change of character', 'market structure', 'shift'] },
  { concept: 'FIBONACCI',    keywords: ['fib', 'fibonacci', 'ote', '0.618', '0.75', '0.15', '0.84', '0.90', 'golden', 'retracement'] },
  { concept: 'SESSION',      keywords: ['london', 'new york', 'ny', 'asian', 'session', 'open', 'close', 'killzone', 'amd'] },
  { concept: 'LIQUIDITY',    keywords: ['liquidity', 'bsl', 'ssl', 'equal highs', 'equal lows', 'hunt', 'sweep'] },
  { concept: 'CONFLUENCE',   keywords: ['confluence', 'multi', 'combination', 'align', 'converge'] },
  { concept: 'MOMENTUM',     keywords: ['trend', 'momentum', 'breakout', 'continuation', 'pullback', 'retest'] },
  { concept: 'PSYCHOLOGY',   keywords: ['psychology', 'emotion', 'discipline', 'patience', 'calm', 'score'] },
  { concept: 'INDIA',        keywords: ['nifty', 'banknifty', 'nse', 'bse', 'india', 'equity', 'fno', 'sensex'] },
];

export function detectStrategyDNA(strategy) {
  const text = [
    strategy.title || '',
    strategy.description || '',
    ...(strategy.conditions || []),
    strategy.entry_logic || '',
    strategy.session_filter || '',
  ].join(' ').toLowerCase();

  for (const pattern of DNA_PATTERNS) {
    if (pattern.keywords.some(kw => text.includes(kw))) {
      return pattern.concept;
    }
  }
  return 'GENERAL'; // fallback
}

// ── Get all strategies that qualify for an active agent ───────────────────────
// "Active" = PROVEN, STRONG, ELITE, HALL_OF_FAME (not HYPOTHESIS/TESTING/RETIRED)

export function getAgentEligibleStrategies() {
  const active = [
    LIFECYCLE_STATUS.PROVEN,
    LIFECYCLE_STATUS.STRONG,
    LIFECYCLE_STATUS.ELITE,
    LIFECYCLE_STATUS.HALL_OF_FAME,
  ];
  return getStrategies()
    .filter(s => active.includes(s.status))
    .map(s => ({
      ...s,
      dna: detectStrategyDNA(s),
      // Weight for Master Brain: based on status + win rate
      agentWeight: calcAgentWeight(s),
    }))
    .sort((a, b) => b.agentWeight - a.agentWeight);
}

function calcAgentWeight(strategy) {
  const wr = strategy.win_rate || 0;
  const total = strategy.total_trades || 0;

  // Status multiplier
  const statusMultiplier = {
    [LIFECYCLE_STATUS.HALL_OF_FAME]: 4.0,
    [LIFECYCLE_STATUS.ELITE]:        3.0,
    [LIFECYCLE_STATUS.STRONG]:       2.5,
    [LIFECYCLE_STATUS.PROVEN]:       2.0,
  }[strategy.status] || 1.0;

  // Sample size bonus (more trades = more reliable)
  const sampleBonus = Math.min(total / 50, 1.0); // caps at 50 trades = 1.0 bonus

  // Win rate score (0-1)
  const wrScore = Math.max(0, (wr - 50) / 50); // 50% = 0, 100% = 1

  return parseFloat((statusMultiplier * (1 + wrScore + sampleBonus * 0.5)).toFixed(2));
}

// ── Version control for strategies ───────────────────────────────────────────
// Before updating a strategy, save old version to lifecycle metadata.

export function saveStrategyVersion(strategy) {
  const meta = getLifecycleMeta();
  const versions = meta[strategy.id]?.versions || [];
  versions.push({
    version: strategy.version || 1,
    savedAt: new Date().toISOString(),
    win_rate: strategy.win_rate,
    total_trades: strategy.total_trades,
    status: strategy.status,
    title: strategy.title,
    conditions: strategy.conditions,
    entry_logic: strategy.entry_logic,
  });
  // Keep only last 5 versions
  setLifecycleMeta({
    ...meta,
    [strategy.id]: {
      ...(meta[strategy.id] || {}),
      versions: versions.slice(-5),
    },
  });
}

export function getStrategyVersionHistory(strategyId) {
  const meta = getLifecycleMeta();
  return meta[strategyId]?.versions || [];
}

// ── Similarity detection ──────────────────────────────────────────────────────
// Two strategies are "similar" if they share the same DNA and
// their condition keywords overlap > 70%.

export function calcStrategySimilarity(s1, s2) {
  if (detectStrategyDNA(s1) !== detectStrategyDNA(s2)) return 0;

  const words1 = new Set(
    [...(s1.conditions || []), s1.title, s1.entry_logic]
      .join(' ').toLowerCase().split(/\W+/).filter(w => w.length > 3)
  );
  const words2 = new Set(
    [...(s2.conditions || []), s2.title, s2.entry_logic]
      .join(' ').toLowerCase().split(/\W+/).filter(w => w.length > 3)
  );

  const intersection = [...words1].filter(w => words2.has(w)).length;
  const union = new Set([...words1, ...words2]).size;
  return union === 0 ? 0 : Math.round((intersection / union) * 100);
}

// Find strategy pairs with > 70% similarity
export function findSimilarStrategies() {
  const strategies = getStrategies().filter(s => s.status !== LIFECYCLE_STATUS.RETIRED);
  const pairs = [];
  for (let i = 0; i < strategies.length; i++) {
    for (let j = i + 1; j < strategies.length; j++) {
      const sim = calcStrategySimilarity(strategies[i], strategies[j]);
      if (sim >= 70) {
        pairs.push({ s1: strategies[i], s2: strategies[j], similarity: sim });
      }
    }
  }
  return pairs.sort((a, b) => b.similarity - a.similarity);
}

// ── Lifecycle summary for UI ──────────────────────────────────────────────────

export function getLifecycleSummary() {
  const all = getStrategies();
  const counts = {};
  Object.values(LIFECYCLE_STATUS).forEach(s => { counts[s] = 0; });
  all.forEach(s => { counts[s.status] = (counts[s.status] || 0) + 1; });

  const eligible = getAgentEligibleStrategies();

  return {
    total:        all.length,
    byStatus:     counts,
    activeAgents: eligible.length,
    topStrategy:  eligible[0] || null,
    avgWinRate:   eligible.length
      ? Math.round(eligible.reduce((s, e) => s + e.win_rate, 0) / eligible.length)
      : 0,
  };
}

// ── Status color + label for UI ───────────────────────────────────────────────

export const STATUS_META = {
  HYPOTHESIS:    { color: '#6b7280', label: 'Hypothesis',     icon: '💡', priority: 0 },
  TESTING:       { color: '#f59e0b', label: 'Testing',        icon: '🧪', priority: 1 },
  PROVEN:        { color: '#3b82f6', label: 'Proven',         icon: '✅', priority: 2 },
  STRONG:        { color: '#8b5cf6', label: 'Strong',         icon: '💪', priority: 3 },
  ELITE:         { color: '#00d4ff', label: 'Elite',          icon: '⭐', priority: 4 },
  HALL_OF_FAME:  { color: '#f59e0b', label: 'Hall of Fame',   icon: '🏆', priority: 5 },
  RETIRED:       { color: '#ef4444', label: 'Retired',        icon: '🔴', priority: -1 },
};

export const DNA_META = {
  ORDER_BLOCK:   { label: 'Order Block',     color: '#ef4444', icon: '📦' },
  FVG:           { label: 'Fair Value Gap',  color: '#f59e0b', icon: '🔷' },
  STRUCTURE:     { label: 'Market Structure',color: '#8b5cf6', icon: '🏗️' },
  FIBONACCI:     { label: 'Fibonacci',       color: '#00d4ff', icon: '🔢' },
  SESSION:       { label: 'Session',         color: '#10b981', icon: '⏰' },
  LIQUIDITY:     { label: 'Liquidity',       color: '#f97316', icon: '💧' },
  CONFLUENCE:    { label: 'Confluence',      color: '#ec4899', icon: '⚡' },
  MOMENTUM:      { label: 'Momentum',        color: '#84cc16', icon: '📈' },
  PSYCHOLOGY:    { label: 'Psychology',      color: '#a78bfa', icon: '🧘' },
  INDIA:         { label: 'India Market',    color: '#f59e0b', icon: '🇮🇳' },
  GENERAL:       { label: 'General',         color: '#6b7280', icon: '🤖' },
};
